import moment from 'moment';

import { useEvent } from '@/components/EventDetailPage/context';
import { Input, TextArea } from '@shared/Form';

import getDistanceLabel from '@/components/RaceSummary/helpers/getDistanceLabel';

import { FieldGroupEnum, FieldEnum } from '../constants';

const safeEval = (f) => {
  try {
    return f();
  } catch (_) {
    console.log(_);
  }
};

const formatStartDate = (startDate, startTime) =>
  startTime
    ? moment(startDate).format('h:mma ddd Do MMM YYYY')
    : moment(startDate).format('ddd Do MMM YYYY');

const getEventStartDate = (e) =>
  safeEval(() => {
    const date = moment(e.startDate);
    const isMidnight = date.format('HH:mm') === '00:00';
    const dateTimeFormat = isMidnight
      ? 'ddd Do MMM YYYY'
      : 'h:mma ddd Do MMM YYYY';

    return date.format(dateTimeFormat);
  });

const getTextFromHtml = (html) => {
  const tag = document.createElement('div');
  tag.innerHTML = html;
  return tag.innerText;
};

const getCustomContent = (e, customContentKey) => {
  return safeEval(() =>
    getTextFromHtml(
      e.races.races[0].customContent.find(
        (content) => content.key === customContentKey,
      ).content,
    ),
  );
};

const createCustomContentField = (customContentKey) => ({ event }) => ({
  initialValue: getCustomContent(event, customContentKey),
  props: {
    rows: 5,
    resizable: true,
  },
  component: TextArea,
});

export const titlesByField = {
  [FieldEnum.TITLE]: 'Event name',
  [FieldEnum.WEBSITE]: 'Event website',
  [FieldEnum.START_DATE]: 'Event date',
  [FieldEnum.LOCATION]: 'Event location',
  [FieldEnum.DESCRIPTION]: 'Event description',
  [FieldEnum.RACE_TITLE]: 'Race name',
  [FieldEnum.RACE_DISTANCE]: 'Race distance',
  [FieldEnum.RACE_START_TIME]: 'Race start time',
  [FieldEnum.COURSE_DETAILS]: 'Route Info',
  [FieldEnum.KIT_LIST]: 'Kit list',
  [FieldEnum.RACEDAY_LOGISTICS]: 'Race day logistics',
  [FieldEnum.REGISTRATION_INFO]: "What's included?",
  [FieldEnum.SPECTATORS]: 'Spectators',
  [FieldEnum.TRAVEL]: 'How to get there',
  [FieldEnum.VOLUNTEERS]: 'Volunteers',
  [FieldEnum.ORGANISER_TITLE]: 'Organiser Name',
  [FieldEnum.ORGANISER_DESCRIPTION]: 'Organiser blurb',
  [FieldEnum.ORGANISER_WEBSITE]: 'Organiser website',
  [FieldEnum.OTHER]: null,
};

export const fieldsByType = {
  [FieldEnum.TITLE]: ({ event }) => ({
    initialValue: event?.title,
    props: {
      required: true,
    },
    component: Input,
  }),
  [FieldEnum.WEBSITE]: ({ event }) => ({
    initialValue: event?.eventUrl,
    component: Input,
  }),
  [FieldEnum.START_DATE]: ({ event }) => ({
    initialValue: getEventStartDate(event),
    component: Input,
  }),
  [FieldEnum.LOCATION]: ({ event }) => ({
    initialValue: event?.location?.address?.join('\n'),
    component: TextArea,
  }),
  [FieldEnum.DESCRIPTION]: ({ event }) => ({
    initialValue: event?.excerpt || getTextFromHtml(event?.description),
    component: TextArea,
  }),
  [FieldEnum.RACE_TITLE]: ({ context }) => ({
    initialValue: context?.race?.title,
    raceId: context?.raceId,
    component: Input,
  }),
  [FieldEnum.RACE_DISTANCE]: ({ context }) => ({
    initialValue: context?.race && getDistanceLabel(context?.race),
    raceId: context?.raceId,
    component: Input,
  }),
  [FieldEnum.RACE_START_TIME]: ({ context }) => ({
    initialValue: safeEval(() =>
      formatStartDate(context?.race?.startDate, context?.race?.startTime),
    ),
    raceId: context?.raceId,
    component: Input,
  }),
  [FieldEnum.COURSE_DETAILS]: createCustomContentField('course_details'),
  [FieldEnum.KIT_LIST]: createCustomContentField('kit_list', 'Kit list'),
  [FieldEnum.RACEDAY_LOGISTICS]: createCustomContentField('raceday_logistics'),
  [FieldEnum.REGISTRATION_INFO]: createCustomContentField('registration_info'),
  [FieldEnum.SPECTATORS]: createCustomContentField('spectators', 'Spectators'),
  [FieldEnum.TRAVEL]: createCustomContentField('travel', 'How to get there'),
  [FieldEnum.VOLUNTEERS]: createCustomContentField('volunteers', 'Volunteers'),
  [FieldEnum.ORGANISER_TITLE]: ({ event }) => ({
    initialValue: event?.organiser?.title,
    component: Input,
  }),
  [FieldEnum.ORGANISER_DESCRIPTION]: ({ event }) => ({
    initialValue: getTextFromHtml(event?.organiser?.description),
    props: {
      rows: 5,
      resizable: true,
    },
    component: TextArea,
  }),
  [FieldEnum.ORGANISER_WEBSITE]: ({ event }) => ({
    initialValue: event?.organiser?.website || event?.eventUrl,
    component: Input,
  }),
  [FieldEnum.OTHER]: () => ({
    props: {
      title: 'Edit',
      hideLabel: true,
      rows: 5,
      resizable: true,
    },
    component: TextArea,
  }),
};

const fieldSubGroup = (fields, title, id) => ({
  fields,
  title,
  id: id || title,
});

export const fieldsByGroup = {
  [FieldGroupEnum.OVERVIEW]: () =>
    fieldSubGroup([
      FieldEnum.TITLE,
      FieldEnum.START_DATE,
      FieldEnum.WEBSITE,
      FieldEnum.DESCRIPTION,
    ]),
  [FieldGroupEnum.COURSE_DETAILS]: () =>
    fieldSubGroup([FieldEnum.COURSE_DETAILS]),
  [FieldGroupEnum.KIT_LIST]: () => fieldSubGroup([FieldEnum.KIT_LIST]),
  [FieldGroupEnum.RACEDAY_LOGISTICS]: () =>
    fieldSubGroup([FieldEnum.RACEDAY_LOGISTICS]),
  [FieldGroupEnum.REGISTRATION_INFO]: () =>
    fieldSubGroup([FieldEnum.REGISTRATION_INFO]),
  [FieldGroupEnum.SPECTATORS]: () => fieldSubGroup([FieldEnum.SPECTATORS]),
  [FieldGroupEnum.HOW_TO_GET_THERE]: () =>
    fieldSubGroup([FieldEnum.LOCATION, FieldEnum.TRAVEL]),
  [FieldGroupEnum.VOLUNTEERS]: () => fieldSubGroup([FieldEnum.VOLUNTEERS]),
  [FieldGroupEnum.RACE_OVERVIEW]: () =>
    fieldSubGroup([
      FieldEnum.RACE_TITLE,
      FieldEnum.RACE_DISTANCE,
      FieldEnum.RACE_START_TIME,
    ]),
  [FieldGroupEnum.ORGANISER]: () =>
    fieldSubGroup([
      FieldEnum.ORGANISER_TITLE,
      FieldEnum.ORGANISER_WEBSITE,
      FieldEnum.ORGANISER_DESCRIPTION,
    ]),
  [FieldGroupEnum.OTHER]: () => fieldSubGroup([FieldEnum.OTHER]),
  [FieldGroupEnum.ALL]: ({ event }) => {
    return [
      fieldSubGroup(
        [
          FieldEnum.TITLE,
          FieldEnum.START_DATE,
          FieldEnum.WEBSITE,
          FieldEnum.DESCRIPTION,
        ],
        'Overview',
      ),
      ...(event?.races?.races?.map((race, i) => {
        return fieldSubGroup(
          [
            {
              type: FieldEnum.RACE_TITLE,
              id: [FieldEnum.RACE_TITLE, i].join('_'),
              raceId: race.id,
              initialValue: race?.title,
            },
            {
              type: FieldEnum.RACE_DISTANCE,
              id: [FieldEnum.RACE_DISTANCE, i].join('_'),
              raceId: race.id,
              initialValue: race && getDistanceLabel(race),
            },
            {
              type: FieldEnum.RACE_START_TIME,
              id: [FieldEnum.RACE_START_TIME, i].join('_'),
              raceId: race.id,
              initialValue: safeEval(() =>
                formatStartDate(race?.startDate, race?.startTime),
              ),
            },
          ],
          `Race ${i + 1}`,
        );
      }) ?? []),
      fieldSubGroup(
        [
          FieldEnum.COURSE_DETAILS,
          FieldEnum.KIT_LIST,
          FieldEnum.RACEDAY_LOGISTICS,
          FieldEnum.REGISTRATION_INFO,
          FieldEnum.SPECTATORS,
          FieldEnum.LOCATION,
          FieldEnum.TRAVEL,
          FieldEnum.VOLUNTEERS,
        ],
        'Additional Details',
      ),
      fieldSubGroup(
        [
          FieldEnum.ORGANISER_TITLE,
          FieldEnum.ORGANISER_WEBSITE,
          FieldEnum.ORGANISER_DESCRIPTION,
        ],
        'Organiser Details',
      ),
      fieldSubGroup([FieldEnum.OTHER], 'Something else?'),
    ];
  },
};

const getFieldType = (f) => (typeof f === 'string' ? f : f.type);
const getFieldOverrides = (f) => (typeof f === 'string' ? {} : f);

export const useGroupEdit = ({ group, context }) => {
  const { event } = useEvent();
  const subGroups = []
    .concat(
      (fieldsByGroup[group] || fieldsByGroup[FieldGroupEnum.OTHER])({
        event,
        context,
      }),
    )
    .map(({ fields: rawFields, title }) => {
      const fields = rawFields
        .filter((f) => fieldsByType[getFieldType(f)])
        .map((f) => {
          const type = getFieldType(f);
          const { props = {}, ...rest } = fieldsByType[type]({
            event,
            context,
          });
          const {
            props: overridesProps = {},
            ...overridesRest
          } = getFieldOverrides(f);
          const mergedProps = {
            title: titlesByField[type],
            ...props,
            ...overridesProps,
          };
          return {
            id: type,
            props: mergedProps,
            ...rest,
            ...overridesRest,
            type,
          };
        });

      return { fields, title };
    });
  const fieldIds = [];
  const initialValues = {};
  const allFields = [];
  subGroups.forEach(({ fields }) => {
    fields.forEach((f) => {
      const { id, initialValue } = f;
      initialValues[id] = initialValue;
      fieldIds.push(id);
      allFields.push(f);
    });
  });

  return { initialValues, fieldIds, subGroups, fields: allFields };
};
